<template>
  <div id="HomeView" class="container max-w-4xl mx-auto pt-10 pb-20 px6">
    <div class="text-center">
      <router-link to="/" class="text-blue-500 no-underline hover:underline">
        Scan Out
      </router-link>
    </div>
    <div v-if="notice != null" class="text-green-700 text-center">{{ notice }}</div>
    <div v-if="error != null" class="text-red-700 text-center">{{ error }}</div>
    <div class="flex flex-wrap mt-4 mb-6">
      <div class="w-full px-3">
        <TextInput 
            label="UID Codes (comma separated if more than 1)"
            id="uid_code"
            placeholder="12345,12346,12347"
            v-model:input="uidCode"
            inputType="text"
            @keyup.enter="searchUdiCode()"
        />
      </div>
    </div>

    <div class="flex flex-wrap mt-8 mb-6">
      <div class="w-full px-3">
        <SubmitFormButton 
            btnText="Search"
            @click="searchUdiCode()"
        />
      </div>
    </div>

    <!-- Orders found by UID code search -->
    <div v-if="orders.length > 0" class="mt-8">
      <h3 class="text-lg font-semibold mb-4">Orders with UID Codes {{ searchedUidCodesDisplay }}</h3>
      <div class="grid gap-4">
        <div v-for="order in orders" :key="order.order_id" 
             class="bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow">
          <div class="grid grid-cols-2 gap-2">
            <div class="text-gray-600">Order Number:</div>
            <div class="font-medium">{{ order.order_id }}</div>
            
            <div class="text-gray-600">Name:</div>
            <div class="font-medium">{{ order.ship_to_name }}</div>
            
            <div class="text-gray-600">Scan Date:</div>
            <div class="font-medium">{{ new Date(order.created_at).toLocaleDateString() }}</div>
            
            <div class="text-gray-600">UID Code:</div>
            <div class="font-medium">
              <template v-if="getUidCodes(order).length > 0">
                <span
                  v-for="(code, idx) in getUidCodes(order)"
                  :key="`${code}-${idx}`"
                  :class="uidCodeClass(code)"
                >
                  {{ code }}<span v-if="idx < getUidCodes(order).length - 1">, </span>
                </span>
              </template>
              <template v-else>no scan</template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sections for each UID code -->
    <div v-for="uidCode in sortedUidCodes" :key="uidCode" class="mt-8">
      <h3 class="text-lg font-semibold mb-4">Orders with UID Code {{ uidCode }}</h3>
      <div class="grid gap-4">
        <div v-for="order in ordersByUidCode[uidCode]" :key="order.order_id" 
             class="bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow">
          <div class="grid grid-cols-2 gap-2">
            <div class="text-gray-600">Order Number:</div>
            <div class="font-medium">{{ order.order_id }}</div>
            
            <div class="text-gray-600">Name:</div>
            <div class="font-medium">{{ order.ship_to_name }}</div>
            
            <div class="text-gray-600">Scan Date:</div>
            <div class="font-medium">{{ new Date(order.created_at).toLocaleDateString() }}</div>
            
            <div class="text-gray-600">UID Code:</div>
            <div class="font-medium">
              <template v-if="getUidCodes(order).length > 0">
                <span
                  v-for="(code, idx) in getUidCodes(order)"
                  :key="`${code}-${idx}`"
                  :class="uidCodeClass(code)"
                >
                  {{ code }}<span v-if="idx < getUidCodes(order).length - 1">, </span>
                </span>
              </template>
              <template v-else>no scan</template>
            </div>
          </div>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script setup>
  import axios from 'axios'
  import { onMounted, ref, computed } from 'vue'
  // import Swal from 'sweetalert2'
  import TextInput from '@/components/global/TextInput.vue'
  import SubmitFormButton from '@/components/global/SubmitFormButton.vue'

  let uidCode = ref(null)
  let orders = ref([])
  let ordersByUidCode = ref({})
  let notice = ref(null)
  let error = ref(null)
  let searchedUidCodes = ref([])

  onMounted(async () => {
      // do nothing
  })

  const searchUdiCode = async () => {
    try {
      let uidCodes = uidCode.value.split(',')
        .map((c) => (c ?? '').toString().trim())
        .filter((c) => c.length > 0)
      searchedUidCodes.value = uidCodes
      notice.value = null
      error.value = null
      ordersByUidCode.value = {}
      
      let res = await axios.post(process.env.VUE_APP_API_URL + 'api/v1/oders_with_uid_codes', {
        uid_codes: uidCodes
      })
      orders.value = res.data
      
      if (orders.value.length > 0) {
        notice.value = "Search matched at least 1 order scanned with UID code " + uidCode.value
        
        // Extract all UID codes from the found orders
        const allUidCodes = new Set()
        orders.value.forEach(order => {
          const orderUidCodes = getUidCodes(order)
          orderUidCodes.forEach(code => allUidCodes.add(code))
        })
        
        // For each UID code, fetch all orders with that code
        if (allUidCodes.size > 0) {
          const uidCodesArray = Array.from(allUidCodes).sort()
          
          // Fetch orders for each UID code
          for (const singleUidCode of uidCodesArray) {
            try {
              const uidRes = await axios.post(process.env.VUE_APP_API_URL + 'api/v1/oders_with_uid_codes', {
                uid_codes: [singleUidCode]
              })
              
              // Store all orders for this UID code (no filtering)
              ordersByUidCode.value[singleUidCode] = uidRes.data || []
            } catch (uidErr) {
              // If UID code search fails for this code, just continue
              console.error(`Error searching for orders with UID code ${singleUidCode}:`, uidErr)
              ordersByUidCode.value[singleUidCode] = []
            }
          }
        }
      } else {
        error.value = "No orders were scanned matching UID code " + uidCode.value
      }
      uidCode.value = null
    } catch (err) {
      // nothing
    }
  }

  const normalizeUidCode = (code) => (code ?? '').toString().trim().toLowerCase()

  const getUidCodes = (order) => {
    const items = Array.isArray(order?.outbound_order_items) ? order.outbound_order_items : []
    return items
      .map((i) => i?.uid_code)
      .filter((c) => typeof c === 'string' && c.trim().length > 0)
      .sort()
  }

  const uidCodeClass = (code) => {
    const searchedSet = new Set(searchedUidCodes.value.map(normalizeUidCode))
    const isMatch = searchedSet.has(normalizeUidCode(code))
    return isMatch ? 'bg-yellow-200 text-black px-1 rounded' : ''
  }

  // Computed property to get sorted UID codes for iteration (excluding searched UID codes)
  const sortedUidCodes = computed(() => {
    const allUidCodes = Object.keys(ordersByUidCode.value)
    const searchedSet = new Set(searchedUidCodes.value.map(normalizeUidCode))
    return allUidCodes
      .filter(code => !searchedSet.has(normalizeUidCode(code)))
      .sort()
  })

  // Computed property to display searched UID codes as comma-separated string
  const searchedUidCodesDisplay = computed(() => {
    return searchedUidCodes.value.length > 0 ? searchedUidCodes.value.join(', ') : ''
  })
</script>
